import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  getAllWorkOrders,
  getPipelineFilters,
  getTopWorkOrders,
  getWorkOrdersList,
} from '../../../api/workOrders';
import {
  GetAllWorkOrderProps,
  GetPipelineFiltersProps,
  GetTopWorkOrdersProps,
  GetWorkOrdersListProps,
  GetWorkOrdersParams,
} from '../../../api/workOrders/types';
import { unwrap } from '../../../lib/envelope';
import { queryClient } from '../../../lib/queryClient';

/* Shared with the other react-query read paths (Hive products). */

/**
 * Read queries of the Home. One key prefix (`work-orders`) so a websocket
 * event or a mutation can invalidate every list at once.
 */
export const workOrderKeys = {
  all: ['work-orders'] as const,
  top: () => [...workOrderKeys.all, 'top'] as const,
  pipeline: (params: GetWorkOrdersParams) =>
    [...workOrderKeys.all, 'pipeline', params] as const,
  list: (search: string, size: number) =>
    [...workOrderKeys.all, 'list', { search, size }] as const,
  filters: (params: GetWorkOrdersParams) =>
    [...workOrderKeys.all, 'filters', params] as const,
};

export { unwrap };

/** Server-side params accepted by `/work-orders` and `/work-orders/filters`. */
export const buildParams = (
  searchField: string,
  filters: GetWorkOrdersParams | undefined
): GetWorkOrdersParams => {
  const params: GetWorkOrdersParams = {};
  if (searchField.length > 0) params.search = searchField;
  if (filters?.uf) params.uf = filters.uf;
  if (filters?.city) params.city = filters.city;
  if (filters?.licenseCompanyId)
    params.licenseCompanyId = filters.licenseCompanyId;
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;
  return params;
};

export const hasServerFilters = (
  filters: GetWorkOrdersParams | undefined
): boolean =>
  Boolean(
    filters?.uf ||
      filters?.city ||
      filters?.licenseCompanyId ||
      filters?.startDate ||
      filters?.endDate
  );

export const useTopWorkOrdersQuery = (
  enabled: boolean
): UseQueryResult<GetTopWorkOrdersProps> =>
  useQuery({
    queryKey: workOrderKeys.top(),
    queryFn: ({ signal }) => getTopWorkOrders(signal).then(unwrap),
    enabled,
  });

export const usePipelineWorkOrdersQuery = (
  params: GetWorkOrdersParams,
  enabled: boolean
): UseQueryResult<GetAllWorkOrderProps> =>
  useQuery({
    queryKey: workOrderKeys.pipeline(params),
    queryFn: ({ signal }) => getAllWorkOrders(params, signal).then(unwrap),
    enabled,
    placeholderData: keepPreviousData,
  });

export const FLAT_PAGE_SIZE = 50;

export const useWorkOrdersListQuery = (
  search: string,
  enabled: boolean
): ReturnType<typeof useInfiniteQuery<GetWorkOrdersListProps>> =>
  useInfiniteQuery<GetWorkOrdersListProps>({
    queryKey: workOrderKeys.list(search, FLAT_PAGE_SIZE),
    queryFn: ({ pageParam, signal }) =>
      getWorkOrdersList(
        pageParam as number,
        FLAT_PAGE_SIZE,
        search,
        signal
      ).then(unwrap),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const current = lastPage.detail.current_page ?? 1;
      const total = lastPage.detail.total_pages ?? 1;
      return current < total ? current + 1 : undefined;
    },
    enabled,
  });

/** Filter vocabularies change rarely: cached for five minutes, shared by the modal. */
export const fetchPipelineFilters = (
  params: GetWorkOrdersParams
): Promise<GetPipelineFiltersProps> =>
  queryClient.fetchQuery({
    queryKey: workOrderKeys.filters(params),
    queryFn: ({ signal }) => getPipelineFilters(params, signal).then(unwrap),
    staleTime: 5 * 60_000,
  });

export const invalidateWorkOrders = (): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
